/**
 * advance-discovery.ts — v4M11.2 per-tick in-system entity discovery
 * pass per `docs/IN_SYSTEM_FOG.md §2.5`.
 *
 * Every tick, for each empire, walks scanners (own + teammate ships
 * and planets) plus LRSA reaches and promotes entities (planets +
 * environment features) within reach into the empire's
 * `discoveredPlanetIds` / `discoveredEnvironmentIds` Sets. Once
 * discovered an entity stays discovered (§2.2 once-discovered-stays);
 * team-vision union per §2.1 propagates writes to teammates at write
 * time.
 *
 * Ordering inside `advanceTick` (`advance.ts`):
 *   1. Combat / construction / bombardment / game-end / AI (existing).
 *   2. `advanceInSystemDiscovery` (this pass) — captures the post-move
 *      ship positions so a ship that crossed into scan range this
 *      tick promotes the entity immediately.
 *   3. `captureFogOfWarSnapshotsForTick` (v4M10.1) — snapshot pass
 *      reads the freshly-updated discovered sets when it eventually
 *      becomes entity-aware (v4M11.3+ territory).
 *   4. `applyLrsaRevealsForTick` (v4M10.6) — LRSA snapshot pass.
 *
 * Strict-parity for v4M11.2: discovered sets get populated but no
 * renderer consumer reads them yet. v4M11.3 wires the system-view
 * draw-loop gates.
 */

import { areTeammates } from './teams';
import { sensorReachForEmpire } from '../state/fow';
import {
  planetScanRangeForSize,
} from './balance-detection';
import { sensorsScanRangeMultiplier } from './research-effects';
import type {
  Empire,
  EmpireId,
  EntityId,
  GameState,
  PlanetId,
  StarSystem,
  SystemId,
} from '../state/types/index';

/**
 * v4M11.2 — derive the in-system scan radius for a ship per `docs/
 * IN_SYSTEM_FOG.md §2.3`. Mirrors the composition `scanners.ts::
 * collectTeamScannersForSystem` already applies for ship-vs-ship
 * detection: `template.scanRange × chassisLevelScale ×
 * sensorsScanRangeMultiplier`.
 *
 * Returns 0 when the template can't be resolved (defensive — the
 * detection pass already short-circuits this case).
 */
function shipEffectiveScanRange(
  state: GameState,
  ship: GameState['ships'] extends Map<unknown, infer V> ? V : never,
): number {
  const empire = state.empires.get(ship.ownerEmpireId);
  if (!empire) return 0;
  const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
  if (!tmpl) return 0;
  let chassisMult = 1.0;
  if (ship.chassisLevel !== undefined && ship.chassisLevel > 1) {
    chassisMult = Math.min(2.0, 1.0 + (ship.chassisLevel - 1) * 0.2);
  }
  return tmpl.scanRange * chassisMult * sensorsScanRangeMultiplier(state, empire);
}

/**
 * v4M11.2 — derive the in-system scan radius for a planet per `docs/
 * IN_SYSTEM_FOG.md §2.3`. Mirrors `scanners.ts::collectTeamScanners
 * ForSystem` post-v4M10.6 retirement of the LRSA additive boost: the
 * planet's scan range now reads as `planetScanRangeForSize ×
 * sensorsScanRangeMultiplier` with no LRSA term.
 *
 * Returns 0 when the empire can't be resolved (defensive — the
 * detection pass already short-circuits this case).
 */
function planetEffectiveScanRange(
  state: GameState,
  planet: GameState['planets'] extends Map<unknown, infer V> ? V : never,
): number {
  if (planet.ownerEmpireId === null) return 0;
  const empire = state.empires.get(planet.ownerEmpireId);
  if (!empire) return 0;
  const base = planetScanRangeForSize(planet.size, state.balanceData);
  return base * sensorsScanRangeMultiplier(state, empire);
}

/**
 * v4M11.2 — bucket every planet + environment entity by its parent
 * system once per tick. The per-tick discovery pass calls this once
 * up front so the per-scanner sweep iterates the candidates in the
 * scanner's own system in O(entities-per-system), not O(total-
 * entities).
 */
interface SystemEntities {
  planetIds: PlanetId[];
  envIds: EntityId[];
}

function indexEntitiesBySystem(state: GameState): Map<SystemId, SystemEntities> {
  const out = new Map<SystemId, SystemEntities>();
  for (const planet of state.planets.values()) {
    let bucket = out.get(planet.systemId);
    if (!bucket) {
      bucket = { planetIds: [], envIds: [] };
      out.set(planet.systemId, bucket);
    }
    bucket.planetIds.push(planet.id);
  }
  for (const env of state.environment.values()) {
    let bucket = out.get(env.systemId);
    if (!bucket) {
      bucket = { planetIds: [], envIds: [] };
      out.set(env.systemId, bucket);
    }
    bucket.envIds.push(env.id);
  }
  return out;
}

/**
 * v4M11.2 — precompute team membership once per tick so the per-
 * entity propagation step is O(teamSize) rather than O(empires).
 */
function indexTeamMembers(state: GameState): Map<EmpireId, EmpireId[]> {
  const out = new Map<EmpireId, EmpireId[]>();
  for (const empireA of state.empires.values()) {
    const members: EmpireId[] = [empireA.id];
    for (const empireB of state.empires.values()) {
      if (empireB.id === empireA.id) continue;
      if (areTeammates(state, empireA.id, empireB.id)) members.push(empireB.id);
    }
    out.set(empireA.id, members);
  }
  return out;
}

/**
 * v4M11.2 — add a planet to every team member's `discoveredPlanetIds`
 * Set. Team-vision union per `IN_SYSTEM_FOG.md §2.1` baked in at
 * write time so consumers (renderer gates in v4M11.3) trust the per-
 * empire Set without re-walking teammates.
 */
function addPlanetToTeam(
  state: GameState,
  teamMembers: EmpireId[],
  planetId: PlanetId,
): void {
  for (const empireId of teamMembers) {
    const empire = state.empires.get(empireId);
    if (!empire) continue;
    empire.discoveredPlanetIds.add(planetId);
  }
}

/**
 * v4M11.2 — companion to `addPlanetToTeam` for environment features.
 */
function addEnvToTeam(
  state: GameState,
  teamMembers: EmpireId[],
  envId: EntityId,
): void {
  for (const empireId of teamMembers) {
    const empire = state.empires.get(empireId);
    if (!empire) continue;
    empire.discoveredEnvironmentIds.add(envId);
  }
}

/**
 * v4M11.2 — for the given scanner position `(sx, sy)` in `sysId`,
 * compare distance to every planet + environment entity in the
 * system; for each entity within `range`, add it (and propagate to
 * teammates) to the discovered sets.
 *
 * Distance squared comparison avoids the sqrt in the hot loop;
 * matches the v4M10.6 `sensorReachForEmpire` boundary-inclusivity
 * pattern (`dx² + dy² ≤ r²`).
 */
function discoverInScanRange(
  state: GameState,
  sysId: SystemId,
  sx: number,
  sy: number,
  range: number,
  teamMembers: EmpireId[],
  systemEntities: Map<SystemId, SystemEntities>,
): void {
  if (range <= 0) return;
  const bucket = systemEntities.get(sysId);
  if (!bucket) return;
  const r2 = range * range;
  for (const pid of bucket.planetIds) {
    const planet = state.planets.get(pid);
    if (!planet) continue;
    const dx = planet.systemX - sx;
    const dy = planet.systemY - sy;
    if (dx * dx + dy * dy > r2) continue;
    addPlanetToTeam(state, teamMembers, pid);
  }
  for (const eid of bucket.envIds) {
    const env = state.environment.get(eid);
    if (!env) continue;
    const dx = env.sx - sx;
    const dy = env.sy - sy;
    if (dx * dx + dy * dy > r2) continue;
    addEnvToTeam(state, teamMembers, eid);
  }
}

/**
 * v4M11.2 — per-tick in-system entity discovery pass per `docs/
 * IN_SYSTEM_FOG.md §2.5`. Runs every tick from `advanceTick` between
 * the existing combat/construction/AI passes and the v4M10.1
 * snapshot capture pass.
 *
 * Three contribution sources per empire:
 *   1. Own ships' scan range (skipping mid-jump and dead-hull ships
 *      — same predicate `scanners.ts` applies).
 *   2. Own planets' scan range.
 *   3. LRSA-revealed systems auto-discover every entity in covered
 *      systems for the LRSA owner per `SENSORS.md §2.2 + §2.6`. Reuses
 *      the v4M10.6 `sensorReachForEmpire` helper.
 *
 * Team-vision union (§2.1) propagates writes at the per-entity level
 * via `teamMembers` lookup so the resulting per-empire `discoveredX
 * Ids` Sets carry the union; consumers read the per-empire Set
 * without re-walking teammates.
 *
 * Idempotent: re-running on an unchanged state produces identical
 * Sets — `Set.add` of an existing member is a no-op.
 */
export function advanceInSystemDiscovery(state: GameState): void {
  const systemEntities = indexEntitiesBySystem(state);
  const teamMembersByEmpire = indexTeamMembers(state);

  for (const empire of state.empires.values()) {
    const teamMembers = teamMembersByEmpire.get(empire.id) ?? [empire.id];

    // 1) Own ships' scan range.
    for (const shipId of empire.shipIds) {
      const ship = state.ships.get(shipId);
      if (!ship) continue;
      // Mid-jump ships are NOT in their target system yet — skip per
      // the same gate `scanners.ts::collectTeamScannersForSystem`
      // applies. `jumpTicksRemaining > 0` is the canonical mid-jump
      // predicate; a defensive `currentHull <= 0` skip mirrors the
      // dead-ship gate in v4M10.2's `currentLayerForSystem`.
      if (ship.jumpTicksRemaining > 0) continue;
      if (ship.currentHull <= 0) continue;
      // v4M11.2 — defensive null-narrow: `ship.systemId: SystemId |
      // null` (null while mid-jump). The mid-jump skip above filters
      // most null cases, but the type system needs the explicit guard.
      if (ship.systemId === null) continue;
      const range = shipEffectiveScanRange(state, ship);
      discoverInScanRange(
        state,
        ship.systemId,
        ship.sx,
        ship.sy,
        range,
        teamMembers,
        systemEntities,
      );
    }

    // 2) Own planets' scan range.
    for (const planetId of empire.planetIds) {
      const planet = state.planets.get(planetId);
      if (!planet) continue;
      if (planet.ownerEmpireId !== empire.id) continue;
      const range = planetEffectiveScanRange(state, planet);
      discoverInScanRange(
        state,
        planet.systemId,
        planet.systemX,
        planet.systemY,
        range,
        teamMembers,
        systemEntities,
      );
    }

    // 3) LRSA-revealed systems auto-discover everything per
    //    `SENSORS.md §2.2 + §2.6`. Reuses v4M10.6
    //    `sensorReachForEmpire` so the schema-to-reach data path
    //    stays single-sourced. Iterates every revealed-system →
    //    every entity in that system, adding to discovered sets
    //    unconditionally (no distance check — LRSA reveal is system-
    //    wide).
    const reach = sensorReachForEmpire(state, empire.id);
    for (const sysId of reach.keys()) {
      const bucket = systemEntities.get(sysId);
      if (!bucket) continue;
      for (const pid of bucket.planetIds) addPlanetToTeam(state, teamMembers, pid);
      for (const eid of bucket.envIds) addEnvToTeam(state, teamMembers, eid);
    }
  }
}

/**
 * v4M11.2 — scenario seeding per `docs/IN_SYSTEM_FOG.md §2.4`. Adds
 * every planet + environment entity in the empire's home system to
 * the empire's discovered sets. Called from `scenario.ts` once the
 * home system has been assigned + populated.
 *
 * Exported so the scenario-gen path can compose the seed without
 * duplicating the bucket-walk logic. Idempotent.
 */
export function seedHomeSystemDiscovery(
  state: GameState,
  empire: Empire,
  homeSystem: StarSystem,
): void {
  for (const pid of homeSystem.planetIds) {
    empire.discoveredPlanetIds.add(pid);
  }
  for (const env of state.environment.values()) {
    if (env.systemId !== homeSystem.id) continue;
    empire.discoveredEnvironmentIds.add(env.id);
  }
}
